

import { useState, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Swal from 'sweetalert2';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    IconButton,
    LinearProgress,
    Alert,
    InputLabel,
    FormControl,
    Select,
    MenuItem,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CancelIcon from '@mui/icons-material/Cancel';
import { addPicturesToMastersheet, uploadRequestedPictures } from './api';

const styleSheet = document.createElement('style');
styleSheet.type = 'text/css';
styleSheet.innerText = `.swal2-container {z-index: 3000 !important;}`;
document.head.appendChild(styleSheet);

const UploadPicture = ({
    isOpen,
    setOpen,
    onClose,
    onPictureUploaded,
    propertyId,
    buildingName,
    unitNo,
    pictureType,
    pictures = []
}) => {
    const [file, setFile] = useState(null);
    const [s3Data, setS3Data] = useState({ url: null, key: null });
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);

    // Filter pictures to show only those with alwaysVisible === false
    const availablePictures = pictures.filter(pic => pic.alwaysVisible === false);
    const pictureTypes = availablePictures.map(pic => pic.picture_type);

    // Determine if we need to show picture type selection
    const showPictureTypeSelection = !pictureType && pictureTypes.length > 0;

    const validationSchema = Yup.object({
        picture_type: showPictureTypeSelection
            ? Yup.string()
                .required('Picture type is required')
                .oneOf(pictureTypes, 'Please select a valid picture type')
            : Yup.string(),
        comments: Yup.string().max(500, 'Comments cannot exceed 500 characters'),
        propertyId: Yup.string().test(
            'is-valid-objectid',
            'Invalid property ID',
            (value) => !value || /^[0-9a-fA-F]{24}$/.test(value)
        ),
    });

    const formik = useFormik({
        initialValues: {
            picture_type: pictureType || '',
            comments: '',
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            if (!file || !s3Data.url || !s3Data.key) {
                setError('File not uploaded to S3');
                return;
            }

            try {
                setUploading(true);

                const pictureData = {
                    picture_type: pictureType || values.picture_type,
                    url: s3Data.url,
                    key: s3Data.key,
                    comments: values.comments || undefined,
                    size: file.size,
                    previous_versions: [],
                };

                const response = await addPicturesToMastersheet(
                    propertyId || undefined,
                    buildingName || undefined,
                    unitNo || undefined,
                    pictureData
                );

                setUploading(false);
                onClose();
                onPictureUploaded(response.data);

                Swal.fire({
                    title: 'Picture Added',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false,
                    customClass: {
                        container: 'swal2-container',
                    },
                });
            } catch (err) {
                console.error('Error adding picture:', err);
                setError(err.response?.data?.message || 'Failed to add picture');
                setUploading(false);
            }
        }
    });

    const uploadToS3 = async (selectedFile) => {
        try {
            setUploading(true);
            setUploadProgress(0);

            const formData = new FormData();
            formData.append('pictures', selectedFile);

            const s3Response = await uploadRequestedPictures(formData, (event) => {
                if (event.lengthComputable) {
                    const progress = Math.round((event.loaded / event.total) * 100);
                    setUploadProgress(progress);
                }
            });

            const { url, key } = s3Response.data[0];
            setS3Data({ url, key });
            setUploading(false);
            setError(null);
        } catch (err) {
            console.error('S3 upload error:', err);
            setError('Failed to upload file to S3');
            setUploading(false);
            setS3Data({ url: null, key: null });
        }
    };

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            // Validate file type
            const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
            if (!validTypes.includes(selectedFile.type)) {
                setError('Invalid file type. Only JPEG, PNG, and GIF are allowed.');
                return;
            }
            setFile(selectedFile);
            setError(null);
            uploadToS3(selectedFile);
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    const handleDrop = (event) => {
        event.preventDefault();
        const droppedFile = event.dataTransfer.files[0];
        if (droppedFile) {
            // Validate file type
            const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
            if (!validTypes.includes(droppedFile.type)) {
                setError('Invalid file type. Only JPEG, PNG, and GIF are allowed.');
                return;
            }
            setFile(droppedFile);
            setError(null);
            uploadToS3(droppedFile);
        }
    };

    const resetUploader = () => {
        setFile(null);
        setS3Data({ url: null, key: null });
        setUploadProgress(0);
        setError(null);
        formik.resetForm();
        setOpen(true);
    };

    const handleCancel = () => {
        Swal.fire({
            title: 'Are you sure?',
            text: 'Are you sure you want to stop uploading this picture?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, stop upload',
            cancelButtonText: 'No, continue',
            customClass: {
                container: 'swal2-container',
            },
        }).then((result) => {
            if (result.isConfirmed) {
                setUploading(false);
                onClose();
            }
        });
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleCancel}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2, p: 1 },
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Add Picture</Typography>
                <IconButton onClick={handleCancel} disabled={uploading}>
                    <CancelIcon fontSize="medium" />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <form onSubmit={formik.handleSubmit}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                        {/* Show picture type selection only if pictureType is not provided */}
                        {showPictureTypeSelection && (
                            <FormControl fullWidth>
                                <InputLabel id="picture-type-label">Picture Type</InputLabel>
                                <Select
                                    labelId="picture-type-label"
                                    id="picture_type"
                                    name="picture_type"
                                    value={formik.values.picture_type}
                                    label="Picture Type"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.picture_type && Boolean(formik.errors.picture_type)}
                                    disabled={uploading}
                                >
                                    {pictureTypes.map((picType) => (
                                        <MenuItem key={picType} value={picType}>
                                            {picType}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {formik.touched.picture_type && formik.errors.picture_type && (
                                    <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                                        {formik.errors.picture_type}
                                    </Typography>
                                )}
                            </FormControl>
                        )}

                        {/* Show picture type as read-only if provided */}
                        {pictureType && (
                            <TextField
                                fullWidth
                                label="Picture Type"
                                value={pictureType}
                                disabled
                                variant="outlined"
                            />
                        )}

                        <TextField
                            fullWidth
                            id="comments"
                            name="comments"
                            label="Comments (Optional)"
                            multiline
                            rows={3}
                            value={formik.values.comments}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.comments && Boolean(formik.errors.comments)}
                            helperText={formik.touched.comments && formik.errors.comments}
                            disabled={uploading}
                        />

                        <FormControl>
                            <InputLabel shrink sx={{ bgcolor: 'background.paper', px: 1, ml: -1 }}>
                                File Upload
                            </InputLabel>
                            <Box
                                sx={{
                                    border: '2px dashed',
                                    borderColor: 'grey.400',
                                    borderRadius: 2,
                                    p: 3,
                                    textAlign: 'center',
                                    bgcolor: 'grey.50',
                                    cursor: 'pointer',
                                    '&:hover': { borderColor: 'grey.600' },
                                }}
                                onClick={() => fileInputRef.current?.click()}
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                            >
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    accept="image/jpeg,image/png,image/gif"
                                    style={{ display: 'none' }}
                                    disabled={uploading}
                                />
                                <CloudUploadIcon sx={{ fontSize: 40, color: 'grey.500', mb: 2 }} />
                                <Typography variant="body1" sx={{ mt: 2 }}>
                                    {file ? file.name : 'Drag & drop picture here or click to browse'}
                                </Typography>
                                {file && (
                                    <Typography variant="body2" color="text.secondary">
                                        {(file.size / 1024 / 1024).toFixed(2)} MB
                                    </Typography>
                                )}
                            </Box>
                        </FormControl>
                    </Box>

                    {uploading && (
                        <Box sx={{ width: '100%', mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2">Uploading...</Typography>
                                <Typography variant="body2">{uploadProgress}%</Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={uploadProgress}
                                sx={{ height: 8, borderRadius: 4 }}
                            />
                        </Box>
                    )}
                </form>
            </DialogContent>

            <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                <Button onClick={handleCancel} disabled={uploading}>
                    Cancel
                </Button>
                <Button
                    onClick={formik.handleSubmit}
                    variant="contained"
                    disabled={!file || !s3Data.url || uploading || (showPictureTypeSelection && !formik.values.picture_type)}
                    startIcon={<CloudUploadIcon />}
                >
                    Add Picture
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export { UploadPicture };